import React, { useEffect, useState, useCallback, useMemo } from "react";
import { UserDetails } from "@components/header-sidebar/user-details";
import { InPageSpinner } from "@components/spinners/in-page-spinner";
import { SecretManagerHandler, SecretManager, SecretItem } from "@api/secret-manager-handler";
import adminStyles from "./../styles.module.scss";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@store/types";
import { errorAlert, successAlert } from "@components/toastify/notify-toast";
import { BsPlusLg, BsSearch } from "react-icons/bs";
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePoweroff, AiOutlineEye } from "react-icons/ai";
import { FiKey } from "react-icons/fi";
import DynamicLeftNav from "../left-nav";
import { Tooltip } from "antd";
import { DeleteConfirm } from "@pages/admin/asset_heirarchy/confirm";
import SecretManagerModal from "./secret-manager-modal";
import { Modal } from "@components/modals";
import { setActiveSecretManagers } from "@store/secret-manager";
const SecretManagerPage: React.FC = () => {
    const [isLoading, setLoadingState] = useState(false);
    const [secretManagers, setSecretManagers] = useState<SecretManager[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedSecret, setSelectedSecret] = useState<SecretManager | null>(null);
        const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [secretToDelete, setSecretToDelete] = useState<SecretManager | null>(null);
    const [showSecretsModal, setShowSecretsModal] = useState(false);
    const [secretsList, setSecretsList] = useState<string[]>([]);
    const [secretsLoading, setSecretsLoading] = useState(false);
    const [selectedSecretName, setSelectedSecretName] = useState("");
    
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const dispatch = useDispatch();

    const getSecretManagers = useCallback(() => {
        if (!Env?.REACT_APP_PLATFORM_URL) return;
        setLoadingState(true);
        SecretManagerHandler.GetAllSecretManagers(
            `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
            (res) => {
                setSecretManagers(res || []);
                const activeSecrets = (res || []).filter((s: SecretManager) => s.active);
                dispatch(setActiveSecretManagers(activeSecrets));
                setLoadingState(false);
            },
            (error) => {
                errorAlert(error?.data?.message || "Failed to fetch secret managers");
                setLoadingState(false);
            }
        );
    }, [Env?.REACT_APP_PLATFORM_URL]);

    useEffect(() => {
        if (Env?.REACT_APP_PLATFORM_URL) {
            getSecretManagers();
        }
    }, [Env?.REACT_APP_PLATFORM_URL, getSecretManagers]);

    const handleCreate = () => {
        setSelectedSecret(null);
        setShowModal(true);
    };

    const handleEdit = (record: SecretManager) => {
        setSelectedSecret(record);
        setShowModal(true);
    };

    const handleDeleteClick = (record: SecretManager) => {
        setSecretToDelete(record);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = () => {
        if (!secretToDelete) return;
        SecretManagerHandler.DeleteSecretManager(
            `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
            secretToDelete.id,
            () => {
                successAlert("Secret deleted successfully");
                getSecretManagers();
                setShowDeleteModal(false);
                setSecretToDelete(null);
            },
            (error) => {
                errorAlert(error?.data?.message || "Failed to delete secret");
            }
        );
    };

    const handleDeleteModalClose = () => {
        setShowDeleteModal(false);
        setSecretToDelete(null);
    };

    const handleActivateToggle = (record: SecretManager) => {
        SecretManagerHandler.ActivateSecretManager(
            `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
            record.id,
            record.active ? 'deactivate' : 'activate',
            () => {
                successAlert(`Secret ${record.active ? 'deactivated' : 'activated'} successfully`);
                getSecretManagers();
            },
            (error) => {
                errorAlert(error?.data?.message || "Failed to update secret status");
            }
        );
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedSecret(null);
    };

    const handleModalSuccess = () => {
        handleModalClose();
        getSecretManagers();
    };

    const handleViewSecrets = (record: SecretManager) => {
        setSelectedSecretName(record.name);
        setSecretsLoading(true);
        setShowSecretsModal(true);
        SecretManagerHandler.GetSecrets(
            `${Env?.REACT_APP_PLATFORM_URL}/vault/v1`,
            record.id,
            (res) => {
                setSecretsList(res?.keys);
                setSecretsLoading(false);
            },
            (error) => {
                errorAlert(error?.data?.message || "Failed to fetch secrets");
                setSecretsLoading(false);
            }
        );
    };

    const handleSecretsModalClose = () => {
        setShowSecretsModal(false);
        setSecretsList([]);
        setSelectedSecretName("");
    };

    const filteredSecrets = useMemo(() => {
        if (!searchTerm) return secretManagers;
        const lowerSearch = searchTerm.toLowerCase();
        return secretManagers.filter(
            (secret) =>
                secret.name?.toLowerCase().includes(lowerSearch) ||
                secret.roleArn?.toLowerCase().includes(lowerSearch) ||
                secret.region?.toLowerCase().includes(lowerSearch) ||
                secret.description?.toLowerCase().includes(lowerSearch)
        );
    }, [secretManagers, searchTerm]);

    const stats = useMemo(() => {
        const total = secretManagers.length;
        const active = secretManagers.filter(s => s.active).length;
        const inactive = total - active;
        return { total, active, inactive };
    }, [secretManagers]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric", 
            year: "numeric" 
        });
    };

    return (
        <section className={classNames(adminStyles["admin__container"])}>
            <DynamicLeftNav selected_id="secret-manager" type={"others"} />
            <div className={styles["secretManagerContainer"]}>
                {/* Header Section */}
                <div className={styles["headerSection"]}>
                    <FiKey className={styles["headerIcon"]} />
                    <div className={styles["headerText"]}>
                        <h1>Secret Manager</h1>
                        <p>Manage your application secrets and API keys</p>
                    </div>
                </div>

                {/* Controls Row */}
                <div className={styles["controlsRow"]}>
                    <div className={styles["searchContainer"]}>
                        <BsSearch className={styles["searchIcon"]} />
                        <input
                            placeholder="Search secrets..."
                            value={searchTerm}
                            disabled={isLoading}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm.length > 0 && (
                            <button 
                                className={styles["clearBtn"]}
                                onClick={() => setSearchTerm("")}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <button className={styles["addButton"]} onClick={handleCreate}>
                        <BsPlusLg/>
                        Secret
                    </button>
                </div>

                {/* Cards */}
                {isLoading ? (
                    <div className={styles["loadingContainer"]}>
                        <InPageSpinner size="Large" color="white" />
                    </div>
                ) : filteredSecrets.length === 0 ? (
                    <div className={styles["emptyState"]}>
                        <FiKey className={styles["emptyIcon"]} />
                        <p>
                            {searchTerm
                                ? "No secrets found matching your search"
                                : "No secrets configured. Click 'Add Secret' to create one."}
                        </p>
                    </div>
                ) : (
                    <div className={styles["cardsContainer"]}>
                        {filteredSecrets.map((secret) => (
                            <div key={secret.id} className={styles["secretCard"]}>
                                <div className={styles["cardHeader"]}>
                                    <div className={styles["cardTitle"]}>
                                        <FiKey className={styles["cardIcon"]} />
                                        <h3>{secret.name}</h3>
                                    </div>
                                    <span className={classNames(
                                        styles["statusBadge"],
                                        secret.active ? styles["active"] : styles["inactive"]
                                    )}>
                                        {secret.active ? "Active" : "Inactive"}
                                    </span>
                                </div>
                                {secret.description && (
                                    <div className={styles["cardDescription"]}>
                                        {secret.description}
                                    </div>
                                )}
                                <div className={styles["cardDetails"]}>
                                    <div className={styles["detailRow"]}>
                                        <span className={styles["detailLabel"]}>Role ARN</span>
                                        <span className={classNames(styles["detailValue"], styles["roleArn"])}>
                                            {secret.roleArn || "-"}
                                        </span>
                                    </div>
                                    <div className={styles["detailRow"]}>
                                        <span className={styles["detailLabel"]}>External ID</span>
                                        <span className={styles["detailValue"]}>{secret.externalId || "-"}</span>
                                    </div>
                                    <div className={styles["detailRow"]}>
                                        <span className={styles["detailLabel"]}>Region</span>
                                        <span className={styles["detailValue"]}>{secret.region || "-"}</span>
                                    </div>
                                    <div className={styles["detailRow"]}>
                                        <span className={styles["detailLabel"]}>Secret Name</span>
                                        <span className={styles["detailValue"]}>{secret.secretName || "-"}</span>
                                    </div>
                                </div>
                                <div className={styles["cardFooter"]}>
                                    <div className={styles["cardActions"]}>
                                        <Tooltip title={secret.active ? "Deactivate" : "Activate"}>
                                            <button 
                                                className={classNames(styles["actionBtn"], styles["activateBtn"])}
                                                onClick={() => handleActivateToggle(secret)}
                                            >
                                                <AiOutlinePoweroff />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <button 
                                                className={classNames(styles["actionBtn"], styles["editBtn"])}
                                                onClick={() => handleEdit(secret)}
                                            >
                                                <AiOutlineEdit />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <button 
                                                className={classNames(styles["actionBtn"], styles["deleteBtn"])}
                                                onClick={() => handleDeleteClick(secret)}
                                            >
                                                <AiOutlineDelete />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="View Secrets">
                                            <button 
                                                className={classNames(styles["actionBtn"], styles["viewBtn"])}
                                                onClick={() => handleViewSecrets(secret)}
                                            >
                                                <AiOutlineEye />
                                            </button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="properties__container properties__container--minimized">
                <UserDetails hideNameOnMinimize={true} />
            </div>
            <SecretManagerModal
                showModal={showModal}
                toggleClose={handleModalClose}
                onSuccess={handleModalSuccess}
                secretData={selectedSecret}
            />
            <DeleteConfirm
                showModal={showDeleteModal}
                toggleClose={handleDeleteModalClose}
                title={`Are you sure you want to delete the secret "${secretToDelete?.name || ""}"?`}
                onConfirm={handleDeleteConfirm}
            />
            <Modal
                title={`Secrets - ${selectedSecretName}`}
                subtitle={"Secrets List"}
                isOpen={showSecretsModal}
                toggleClose={handleSecretsModalClose}
                showCloseMark={true}
            >
                <div className={styles["secretsModalContent"]}>
                    {secretsLoading ? (
                        <div className={styles["secretsLoading"]}>
                            <InPageSpinner size="Small" color="white" />
                        </div>
                    ) : secretsList.length === 0 ? (
                        <div className={styles["secretsEmpty"]}>
                            No secrets found
                        </div>
                    ) : (
                        <ul className={styles["secretsList"]}>
                            {secretsList.map((secret, index) => (
                                <li key={index} className={styles["secretItem"]}>
                                    <FiKey className={styles["secretItemIcon"]} />
                                    <div className={styles["secretItemDetails"]}>
                                        <span className={styles["secretItemName"]}>{secret}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </Modal>
        </section>
    );
};

export default SecretManagerPage;
